import { useMemo, useState } from "react";
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation } from "@tanstack/react-query";
import { AlertTriangle, CheckCircle2 } from "lucide-react";
import { SiteLayout } from "@/components/SiteLayout";
import { Section } from "@/components/Section";
import { PageHero } from "@/components/PageHero";
import { useDeliveryAreas } from "@/hooks/useDeliveryAreas";
import { useMealPlans } from "@/hooks/useMealPlans";
import { submitOrderRequest } from "@/lib/submissions/order.functions";
import { getPublicNotificationSettings } from "@/lib/cms/site-settings.functions";
import { getPublicMp2Mode } from "@/lib/public/meal-plans-v2.functions";
import { trackCtaClick } from "@/lib/analytics";
import { track } from "@/lib/analytics/events";
import { useQuery } from "@tanstack/react-query";
import {
  MEAL_TYPE_OPTIONS,
  type MealType,
} from "@/lib/admin/orders.schemas";

const SITE_URL =
  (import.meta.env.VITE_SITE_URL as string | undefined) ??
  "https://poonamkapur.com";
const CANONICAL = `${SITE_URL}/order`;
const META_TITLE =
  "Order — Poonam Kapur Healthy Kitchen | Fresh Mumbai Meal Delivery";
const META_DESCRIPTION =
  "Request a fresh, home-style meal from Poonam Kapur Healthy Kitchen. Choose a plan, meal type and delivery slot — our team confirms availability and details.";

export const Route = createFileRoute("/order/")({
  // When Meal Plans 2.0 is LIVE the legacy request form is retired from the
  // public site — visitors land on /meal-plans instead. Admins keep visibility
  // into legacy orders through /admin/orders regardless of mode.
  beforeLoad: async () => {
    try {
      const mode = await getPublicMp2Mode();
      if (mode === "live") throw redirect({ to: "/meal-plans" });
    } catch (err) {
      // Re-throw router redirects; swallow anything else so a Supabase blip
      // doesn't blank the legacy order form.
      if (err && typeof err === "object" && "isRedirect" in (err as object)) throw err;
    }
  },
  head: () => ({
    meta: [
      { title: META_TITLE },
      { name: "description", content: META_DESCRIPTION },
      { property: "og:title", content: META_TITLE },
      { property: "og:description", content: META_DESCRIPTION },
      { property: "og:url", content: CANONICAL },
      { property: "og:type", content: "website" },
      { name: "robots", content: "noindex, nofollow" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: META_TITLE },
      { name: "twitter:description", content: META_DESCRIPTION },
    ],
    links: [{ rel: "canonical", href: CANONICAL }],
  }),
  component: OrderPage,
});

function mumbaiToday(): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());
}

const inputClass =
  "w-full rounded-md border border-input bg-background px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground/70 outline-none transition-colors focus-visible:border-primary focus-visible:ring-2 focus-visible:ring-primary/30 aria-[invalid=true]:border-destructive disabled:cursor-not-allowed disabled:opacity-60";

type FormValues = {
  customer_name: string;
  phone: string;
  email: string;
  address_line_1: string;
  address_line_2: string;
  pincode: string;
  delivery_area_id: string;
  meal_plan_id: string;
  meal_type: MealType;
  quantity: number;
  preferred_delivery_date: string;
  preferred_delivery_time: string;
  special_instructions: string;
  cutoff_accepted: boolean;
  whatsapp_opt_in: boolean;
};

const INITIAL: FormValues = {
  customer_name: "",
  phone: "",
  email: "",
  address_line_1: "",
  address_line_2: "",
  pincode: "",
  delivery_area_id: "",
  meal_plan_id: "",
  meal_type: "veg",
  quantity: 1,
  preferred_delivery_date: "",
  preferred_delivery_time: "",
  special_instructions: "",
  cutoff_accepted: false,
  whatsapp_opt_in: false,
};

type Errors = Partial<Record<keyof FormValues, string>>;

function validate(v: FormValues, minDate: string): Errors {
  const e: Errors = {};
  if (v.customer_name.trim().length < 2) e.customer_name = "Please enter your name.";
  const digits = v.phone.replace(/\D/g, "");
  if (digits.length < 10 || digits.length > 15) e.phone = "Enter a valid mobile number.";
  if (v.email.trim().length > 0 && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.email.trim()))
    e.email = "Enter a valid email or leave blank.";
  const l1 = v.address_line_1.trim();
  if (l1.length < 5) e.address_line_1 = "Enter your street address (house / building, street).";
  else if (l1.length > 250) e.address_line_1 = "Address line 1 is too long (max 250).";
  if (v.address_line_2.trim().length > 250) e.address_line_2 = "Address line 2 is too long (max 250).";
  if (!/^[1-9][0-9]{5}$/.test(v.pincode.trim())) e.pincode = "Enter a valid 6-digit pincode.";
  if (!v.delivery_area_id) e.delivery_area_id = "Choose your neighbourhood.";
  if (!v.meal_plan_id) e.meal_plan_id = "Choose a meal plan.";
  if (!Number.isFinite(v.quantity) || v.quantity < 1 || v.quantity > 200)
    e.quantity = "Quantity must be between 1 and 200.";
  if (!v.preferred_delivery_date) e.preferred_delivery_date = "Pick a delivery date.";
  else if (v.preferred_delivery_date < minDate)
    e.preferred_delivery_date = "Date must be today or later (Mumbai time).";
  if (!v.preferred_delivery_time) e.preferred_delivery_time = "Pick a delivery time.";
  if (!v.cutoff_accepted) e.cutoff_accepted = "Please acknowledge the cutoff notice.";
  return e;
}

function OrderPage() {
  const mealPlans = useMealPlans();
  const deliveryAreas = useDeliveryAreas();
  const navigate = useNavigate();
  const submit = useServerFn(submitOrderRequest);
  const publicFlagsFn = useServerFn(getPublicNotificationSettings);
  const publicFlags = useQuery({
    queryKey: ["public-notif-flags"],
    queryFn: () => publicFlagsFn(),
    staleTime: 5 * 60 * 1000,
  });
  const showWhatsAppOptIn = Boolean(publicFlags.data?.whatsappCustomerEnabled);

  const [submissionId, setSubmissionId] = useState<string>(() =>
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`,
  );
  const [values, setValues] = useState<FormValues>(INITIAL);
  const [errors, setErrors] = useState<Errors>({});
  const [honeypot, setHoneypot] = useState("");
  const [success, setSuccess] = useState<{ orderRef: string | null } | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const minDate = useMemo(() => mumbaiToday(), []);

  const dataUnavailable =
    mealPlans.isFallback ||
    (!mealPlans.isLoading && mealPlans.plans.length === 0) ||
    deliveryAreas.isFallback ||
    deliveryAreas.isEmpty;

  const dataLoading = mealPlans.isLoading || deliveryAreas.isLoading;

  const setField = <K extends keyof FormValues>(k: K, val: FormValues[K]) => {
    setValues((v) => ({ ...v, [k]: val }));
    if (errors[k]) setErrors((e) => ({ ...e, [k]: undefined }));
  };

  const mutation = useMutation({
    mutationFn: async () => {
      return submit({
        data: {
          submission_id: submissionId,
          customer_name: values.customer_name.trim(),
          phone: values.phone.trim(),
          email: values.email.trim() || undefined,
          address_line_1: values.address_line_1.trim(),
          address_line_2: values.address_line_2.trim() || null,
          pincode: values.pincode.trim(),
          delivery_area_id: values.delivery_area_id,
          meal_plan_id: values.meal_plan_id,
          meal_type: values.meal_type,
          quantity: values.quantity,
          preferred_delivery_date: values.preferred_delivery_date,
          preferred_delivery_time: values.preferred_delivery_time,
          special_instructions: values.special_instructions.trim(),
          cutoff_accepted: true as const,
          honeypot,
          whatsapp_opt_in: values.whatsapp_opt_in,
          whatsapp_opt_in_source: "order_form",
          whatsapp_opt_in_text_version: "v1",
        },
      });
    },
    onSuccess: (result) => {
      if (result.stored && !result.idempotent) {
        trackCtaClick({
          action: "order_request_submitted",
          location: "order",
          label: result.orderRef ?? "order",
        });
        const areaName =
          deliveryAreas.areas.find((a) => a.id === values.delivery_area_id)?.neighbourhood ?? "";
        track("order_submitted", {
          order_type: values.meal_type,
          item_count: values.quantity,
          delivery_area_name: areaName,
          customer_type: values.email ? "email" : "phone_only",
          success: true,
        });
      }
      if (result.paymentToken) {
        console.info("[order] redirecting to /order/pay", { hasToken: true });
        const url = `/order/pay?token=${encodeURIComponent(result.paymentToken)}`;
        try {
          void navigate({
            to: "/order/pay",
            search: { token: result.paymentToken },
          });
        } catch (e) {
          console.error("[order] navigate failed, falling back", e);
        }
        // Hard fallback in case client-side navigation is intercepted or
        // the router promise is dropped after the success render.
        window.setTimeout(() => {
          if (window.location.pathname !== "/order/pay") {
            window.location.assign(url);
          }
        }, 150);
        return;
      }
      setSuccess({ orderRef: result.orderRef });
    },
    onError: (err) => {
      console.error("[order] submit failed", err);
      const msg = err instanceof Error ? err.message : "";
      const category =
        /pincode_invalid|address_line|delivery_date/.test(msg)
          ? "validation"
          : /delivery/.test(msg)
            ? "delivery_area"
            : /network|fetch|timeout/i.test(msg)
              ? "network"
              : "unknown";
      track("order_submission_failed", { error_category: category });
      let friendly: string;
      if (msg === "delivery_date_in_past") {
        friendly = "Delivery date must be today or later (Mumbai time).";
      } else if (/pincode_invalid/.test(msg)) {
        friendly = "Enter a valid 6-digit pincode.";
      } else if (/address_line_1_invalid/.test(msg)) {
        friendly = "Please enter a complete street address (5–250 characters).";
      } else if (/address_line_2_invalid/.test(msg)) {
        friendly = "Address line 2 is too long (max 250 characters).";
      } else if (msg.startsWith("Order submission failed:")) {
        friendly = msg;
      } else {
        friendly =
          "We couldn't submit your request right now. Please try again in a moment.";
      }
      setFormError(friendly);
    },
  });

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (mutation.isPending || dataUnavailable || dataLoading) return;
    setFormError(null);
    const v = validate(values, minDate);
    if (Object.keys(v).length > 0) {
      setErrors(v);
      return;
    }
    setErrors({});
    mutation.mutate();
  };

  const startNew = () => {
    setSuccess(null);
    setValues(INITIAL);
    setErrors({});
    setHoneypot("");
    setFormError(null);
    setSubmissionId(
      typeof crypto !== "undefined" && "randomUUID" in crypto
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2)}`,
    );
  };

  const submitDisabled =
    mutation.isPending || dataUnavailable || dataLoading;

  return (
    <SiteLayout>
      <PageHero
        eyebrow="Order"
        title="Request a fresh, home-style meal."
        intro="Tell us what you'd like and when you'd like it — our team confirms availability, timing and final details before your meal is prepared."
      />

      <Section>
        <div className="mx-auto max-w-2xl">
          {success ? (
            <div
              role="status"
              aria-live="polite"
              className="rounded-3xl border border-border bg-card p-6 md:p-8"
            >
              <div className="flex items-center gap-2 text-primary">
                <CheckCircle2 className="h-5 w-5" aria-hidden />
                <span className="text-xs uppercase tracking-[0.2em]">
                  Request received
                </span>
              </div>
              <h2 className="mt-3 font-display text-2xl font-medium md:text-3xl">
                Your order request has been received.
              </h2>
              <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
                Our team will confirm availability, timing and final details.
              </p>
              {success.orderRef ? (
                <p className="mt-4 text-sm">
                  Reference:{" "}
                  <span className="font-mono font-semibold text-foreground">
                    {success.orderRef}
                  </span>
                </p>
              ) : null}
              <p className="mt-4 text-xs text-muted-foreground">
                Submitting this request does not confirm the order or payment.
              </p>
              <div className="mt-6">
                <button
                  type="button"
                  onClick={startNew}
                  className="inline-flex items-center rounded-full border border-border px-6 py-3 text-sm font-medium text-foreground transition-colors hover:bg-muted"
                >
                  Start a new request
                </button>
              </div>
            </div>
          ) : (
            <>
              {dataUnavailable ? (
                <div
                  role="alert"
                  className="mb-6 flex gap-3 rounded-2xl border border-destructive/30 bg-destructive/5 p-4 text-sm text-foreground"
                >
                  <AlertTriangle
                    className="mt-0.5 h-5 w-5 shrink-0 text-destructive"
                    aria-hidden
                  />
                  <p>
                    Ordering is temporarily unavailable because we could not
                    load the current meal plans or delivery areas. Please try
                    again shortly.
                  </p>
                </div>
              ) : null}

              <form
                onSubmit={handleSubmit}
                noValidate
                className="rounded-3xl border border-border bg-card p-6 md:p-8"
              >
                <fieldset disabled={dataUnavailable || dataLoading} className="grid gap-5">
                  {/* Honeypot */}
                  <div
                    aria-hidden="true"
                    style={{
                      position: "absolute",
                      left: "-10000px",
                      width: "1px",
                      height: "1px",
                      overflow: "hidden",
                    }}
                  >
                    <label htmlFor="o-company">Company (leave blank)</label>
                    <input
                      id="o-company"
                      name="company"
                      type="text"
                      tabIndex={-1}
                      autoComplete="off"
                      value={honeypot}
                      onChange={(e) => setHoneypot(e.target.value)}
                    />
                  </div>

                  <div className="grid gap-5 sm:grid-cols-2">
                    <div className="grid gap-2">
                      <label htmlFor="o-name" className="text-sm font-medium">
                        Name <span aria-hidden className="text-destructive">*</span>
                      </label>
                      <input
                        id="o-name"
                        type="text"
                        autoComplete="name"
                        required
                        maxLength={80}
                        value={values.customer_name}
                        onChange={(e) => setField("customer_name", e.target.value)}
                        aria-invalid={!!errors.customer_name}
                        className={inputClass}
                      />
                      {errors.customer_name && (
                        <p className="text-xs text-destructive">{errors.customer_name}</p>
                      )}
                    </div>
                    <div className="grid gap-2">
                      <label htmlFor="o-phone" className="text-sm font-medium">
                        Mobile <span aria-hidden className="text-destructive">*</span>
                      </label>
                      <input
                        id="o-phone"
                        type="tel"
                        inputMode="tel"
                        autoComplete="tel"
                        required
                        placeholder="+91 …"
                        value={values.phone}
                        onChange={(e) => setField("phone", e.target.value)}
                        aria-invalid={!!errors.phone}
                        className={inputClass}
                      />
                      {errors.phone && (
                        <p className="text-xs text-destructive">{errors.phone}</p>
                      )}
                    </div>
                  </div>

                  <div className="grid gap-2">
                    <label htmlFor="o-email" className="text-sm font-medium">
                      Email <span className="text-muted-foreground">(optional)</span>
                    </label>
                    <input
                      id="o-email"
                      type="email"
                      autoComplete="email"
                      placeholder="you@example.com"
                      value={values.email}
                      onChange={(e) => setField("email", e.target.value)}
                      aria-invalid={!!errors.email}
                      className={inputClass}
                    />
                    {errors.email && (
                      <p className="text-xs text-destructive">{errors.email}</p>
                    )}
                  </div>

                  <div className="grid gap-2">
                    <label htmlFor="o-addr1" className="text-sm font-medium">
                      Address line 1 <span aria-hidden className="text-destructive">*</span>
                    </label>
                    <textarea
                      id="o-addr1"
                      rows={2}
                      maxLength={250}
                      required
                      autoComplete="street-address"
                      placeholder="Flat / house no., building, street"
                      value={values.address_line_1}
                      onChange={(e) => setField("address_line_1", e.target.value)}
                      aria-invalid={!!errors.address_line_1}
                      className={inputClass}
                    />
                    {errors.address_line_1 && (
                      <p className="text-xs text-destructive">{errors.address_line_1}</p>
                    )}
                  </div>

                  <div className="grid gap-5 sm:grid-cols-[1fr_10rem]">
                    <div className="grid gap-2">
                      <label htmlFor="o-addr2" className="text-sm font-medium">
                        Address line 2 <span className="text-muted-foreground">(optional)</span>
                      </label>
                      <input
                        id="o-addr2"
                        type="text"
                        maxLength={250}
                        autoComplete="address-line2"
                        placeholder="Landmark, area (optional)"
                        value={values.address_line_2}
                        onChange={(e) => setField("address_line_2", e.target.value)}
                        aria-invalid={!!errors.address_line_2}
                        className={inputClass}
                      />
                      {errors.address_line_2 && (
                        <p className="text-xs text-destructive">{errors.address_line_2}</p>
                      )}
                    </div>
                    <div className="grid gap-2">
                      <label htmlFor="o-pin" className="text-sm font-medium">
                        Pincode <span aria-hidden className="text-destructive">*</span>
                      </label>
                      <input
                        id="o-pin"
                        type="text"
                        inputMode="numeric"
                        pattern="[1-9][0-9]{5}"
                        maxLength={6}
                        required
                        autoComplete="postal-code"
                        placeholder="400001"
                        value={values.pincode}
                        onChange={(e) =>
                          setField("pincode", e.target.value.replace(/\D/g, "").slice(0, 6))
                        }
                        aria-invalid={!!errors.pincode}
                        className={inputClass}
                      />
                      {errors.pincode && (
                        <p className="text-xs text-destructive">{errors.pincode}</p>
                      )}
                    </div>
                  </div>

                  <div className="grid gap-5 sm:grid-cols-2">
                    <div className="grid gap-2">
                      <label htmlFor="o-area" className="text-sm font-medium">
                        Neighbourhood <span aria-hidden className="text-destructive">*</span>
                      </label>
                      <select
                        id="o-area"
                        required
                        value={values.delivery_area_id}
                        onChange={(e) => setField("delivery_area_id", e.target.value)}
                        aria-invalid={!!errors.delivery_area_id}
                        className={inputClass}
                      >
                        <option value="">Select…</option>
                        {deliveryAreas.areas.map((a) => (
                          <option key={a.id} value={a.id}>
                            {a.neighbourhood}
                          </option>
                        ))}
                      </select>
                      {errors.delivery_area_id && (
                        <p className="text-xs text-destructive">
                          {errors.delivery_area_id}
                        </p>
                      )}
                    </div>
                    <div className="grid gap-2">
                      <label htmlFor="o-plan" className="text-sm font-medium">
                        Meal plan <span aria-hidden className="text-destructive">*</span>
                      </label>
                      <select
                        id="o-plan"
                        required
                        value={values.meal_plan_id}
                        onChange={(e) => setField("meal_plan_id", e.target.value)}
                        aria-invalid={!!errors.meal_plan_id}
                        className={inputClass}
                      >
                        <option value="">Select…</option>
                        {mealPlans.plans.map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.category} — {p.title}
                            {p.priceInPaise > 0 ? ` — ₹${(p.priceInPaise / 100).toFixed(2)}` : " — Enquiry"}
                          </option>
                        ))}
                      </select>
                      {errors.meal_plan_id && (
                        <p className="text-xs text-destructive">{errors.meal_plan_id}</p>
                      )}
                      {(() => {
                        const selected = mealPlans.plans.find((p) => p.id === values.meal_plan_id);
                        if (!selected) return null;
                        if (selected.priceInPaise > 0) {
                          const unit = selected.priceInPaise / 100;
                          const total = (selected.priceInPaise * values.quantity) / 100;
                          return (
                            <p className="text-xs text-muted-foreground">
                              ₹{unit.toFixed(2)} per serving · Total ₹{total.toFixed(2)} for {values.quantity} · Pay online after submitting.
                            </p>
                          );
                        }
                        return (
                          <p className="text-xs text-muted-foreground">
                            This plan is enquiry-based. Our team will confirm pricing.
                          </p>
                        );
                      })()}
                    </div>
                  </div>

                  <div className="grid gap-5 sm:grid-cols-2">
                    <div className="grid gap-2">
                      <span className="text-sm font-medium">
                        Meal type <span className="text-muted-foreground">(preference)</span>
                      </span>
                      <div
                        role="radiogroup"
                        aria-label="Meal type preference"
                        className="flex gap-4 pt-1"
                      >
                        {MEAL_TYPE_OPTIONS.map((o) => (
                          <label
                            key={o.value}
                            className="inline-flex items-center gap-2 text-sm"
                          >
                            <input
                              type="radio"
                              name="meal_type"
                              value={o.value}
                              checked={values.meal_type === o.value}
                              onChange={() => setField("meal_type", o.value)}
                            />
                            <span>{o.label}</span>
                          </label>
                        ))}
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Preference — final availability will be confirmed by our team.
                      </p>
                    </div>
                    <div className="grid gap-2">
                      <label htmlFor="o-qty" className="text-sm font-medium">
                        Quantity <span aria-hidden className="text-destructive">*</span>
                      </label>
                      <input
                        id="o-qty"
                        type="number"
                        min={1}
                        max={200}
                        required
                        value={values.quantity}
                        onChange={(e) =>
                          setField(
                            "quantity",
                            Math.max(1, Math.min(200, Number(e.target.value) || 1)),
                          )
                        }
                        aria-invalid={!!errors.quantity}
                        className={inputClass}
                      />
                      {errors.quantity && (
                        <p className="text-xs text-destructive">{errors.quantity}</p>
                      )}
                    </div>
                  </div>

                  <div className="grid gap-5 sm:grid-cols-2">
                    <div className="grid gap-2">
                      <label htmlFor="o-date" className="text-sm font-medium">
                        Delivery date <span aria-hidden className="text-destructive">*</span>
                      </label>
                      <input
                        id="o-date"
                        type="date"
                        min={minDate}
                        required
                        value={values.preferred_delivery_date}
                        onChange={(e) =>
                          setField("preferred_delivery_date", e.target.value)
                        }
                        aria-invalid={!!errors.preferred_delivery_date}
                        className={inputClass}
                      />
                      {errors.preferred_delivery_date && (
                        <p className="text-xs text-destructive">
                          {errors.preferred_delivery_date}
                        </p>
                      )}
                    </div>
                    <div className="grid gap-2">
                      <label htmlFor="o-time" className="text-sm font-medium">
                        Delivery time <span aria-hidden className="text-destructive">*</span>
                      </label>
                      <input
                        id="o-time"
                        type="time"
                        required
                        value={values.preferred_delivery_time}
                        onChange={(e) =>
                          setField("preferred_delivery_time", e.target.value)
                        }
                        aria-invalid={!!errors.preferred_delivery_time}
                        className={inputClass}
                      />
                      {errors.preferred_delivery_time && (
                        <p className="text-xs text-destructive">
                          {errors.preferred_delivery_time}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="grid gap-2">
                    <label htmlFor="o-notes" className="text-sm font-medium">
                      Special instructions
                    </label>
                    <textarea
                      id="o-notes"
                      rows={3}
                      maxLength={2000}
                      placeholder="Allergies, preferences, delivery notes…"
                      value={values.special_instructions}
                      onChange={(e) => setField("special_instructions", e.target.value)}
                      className={inputClass}
                    />
                    <p className="text-xs text-muted-foreground">
                      Optional — up to 2000 characters. {values.special_instructions.length}/2000
                    </p>
                  </div>

                  <div className="rounded-md border border-border bg-secondary/40 p-3">
                    <label className="flex items-start gap-3 text-sm">
                      <input
                        type="checkbox"
                        className="mt-0.5"
                        checked={values.cutoff_accepted}
                        onChange={(e) => setField("cutoff_accepted", e.target.checked)}
                        aria-invalid={!!errors.cutoff_accepted}
                      />
                      <span>
                        I understand that same-day slots have cutoffs (Breakfast 8:00 pm
                        previous day, Lunch 6:00 am same day, Snacks 8:30 pm previous day,
                        Dinner 12:01 pm same day) and that final availability will be
                        confirmed by the team.
                      </span>
                    </label>
                    {errors.cutoff_accepted && (
                      <p className="mt-2 text-xs text-destructive">
                        {errors.cutoff_accepted}
                      </p>
                    )}
                  </div>

                  {showWhatsAppOptIn ? (
                    <div className="rounded-md border border-border bg-secondary/40 p-3">
                      <label className="flex items-start gap-3 text-sm">
                        <input
                          type="checkbox"
                          className="mt-0.5"
                          checked={values.whatsapp_opt_in}
                          onChange={(e) => setField("whatsapp_opt_in", e.target.checked)}
                        />
                        <span>
                          Send me order updates on WhatsApp at this mobile number.
                          <span className="mt-1 block text-xs text-muted-foreground">
                            Optional. Standard message rates may apply. You can opt out at any time.
                          </span>
                        </span>
                      </label>
                    </div>
                  ) : null}

                  <button
                    type="submit"
                    disabled={submitDisabled}
                    aria-disabled={submitDisabled}
                    className="mt-2 inline-flex items-center justify-center rounded-full bg-primary px-6 py-3 text-sm font-medium text-primary-foreground shadow-sm transition-transform duration-300 hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-70 motion-reduce:transform-none"
                  >
                    {mutation.isPending ? "Submitting…" : "Submit Order Request"}
                  </button>
                  <p className="text-xs text-muted-foreground">
                    Submitting this request does not confirm the order or payment.
                    Our team will follow up to confirm details.
                  </p>
                  <div role="status" aria-live="polite" className="min-h-[1rem]">
                    {formError ? (
                      <p className="text-xs text-destructive">{formError}</p>
                    ) : null}
                  </div>
                </fieldset>
              </form>
            </>
          )}
        </div>
      </Section>
    </SiteLayout>
  );
}